import React, { useState } from 'react';
import { FileText, FileSpreadsheet, Download, X } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import Reveal from './Reveal';

const WEB3FORMS_ACCESS_KEY = '218c02ca-e1cf-4fff-9654-dc8c9b8a5469';

// Real report files served from /public/files
const reports = [
  { title: 'Contractor report', format: 'PDF', desc: 'Full priced estimate formatted for contractors.', file: '/files/core-contractor-report.pdf', icon: FileText },
  { title: 'Client report', format: 'PDF', desc: 'Clean, client-facing summary of the estimate.', file: '/files/core-client-report.pdf', icon: FileText },
  { title: 'Contractor report', format: 'Excel', desc: 'Editable, itemised estimate for contractors.', file: '/files/core-contractor-report.xlsx', icon: FileSpreadsheet },
  { title: 'Client report', format: 'Excel', desc: 'Editable client summary in spreadsheet form.', file: '/files/core-client-report.xlsx', icon: FileSpreadsheet },
];

const triggerDownload = (file) => {
  const a = document.createElement('a');
  a.href = file;
  a.download = '';
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const ReportsPage = () => {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const openFor = (report) => {
    setSelected(report);
    setForm({ name: '', email: '', phone: '' });
    setError('');
    setDone(false);
  };

  const close = () => setSelected(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email');
      return;
    }
    if (!form.phone.trim()) {
      setError('Please enter your number');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('access_key', WEB3FORMS_ACCESS_KEY);
      fd.append('subject', `Report download — ${selected.format} ${selected.title}`);
      fd.append('from_name', 'Core Estimating Website');
      fd.append('name', form.name || 'Not provided');
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('report', `${selected.format} ${selected.title}`);
      fd.append('message', `${form.name || 'Someone'} downloaded the ${selected.format} ${selected.title}.\n\nEmail: ${form.email}\nPhone: ${form.phone}`);
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      });
    } catch (_) {
      // even if lead capture fails, still let them download
    } finally {
      setSubmitting(false);
      triggerDownload(selected.file);
      setDone(true);
    }
  };

  return (
    <main className="bg-[#262629] pt-40 md:pt-48 pb-24 px-5 md:px-10 min-h-screen">
      <div className="max-w-[1400px] mx-auto">
        <Reveal>
          <p className="font-display text-white/50 text-sm tracking-[0.2em] uppercase mb-3">Our reports</p>
          <h1 className="font-display text-yellow text-5xl md:text-7xl mb-5">Sample reports</h1>
          <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mb-12">
            See the standard of work we deliver. Enter your email to download an example in the format that
            suits you — contractor and client versions available in both PDF and Excel.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {reports.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <button
                  onClick={() => openFor(r)}
                  className="group w-full text-left flex items-center gap-5 rounded-2xl bg-[#353539] border border-white/5 p-6 md:p-7 hover:border-yellow/50 transition-colors"
                >
                  <span className="w-14 h-14 rounded-[12px] bg-jaw-yellow flex items-center justify-center shrink-0">
                    <Icon className="w-7 h-7 text-[#262629]" strokeWidth={2} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-display text-white text-2xl md:text-3xl leading-none">{r.title}</span>
                    <span className="block text-yellow text-sm font-semibold mt-1.5">{r.format} download</span>
                    <span className="block text-white/60 text-sm mt-1">{r.desc}</span>
                  </span>
                  <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-white group-hover:bg-jaw-yellow group-hover:text-[#262629] transition-colors">
                    <Download className="w-5 h-5" strokeWidth={2.5} />
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <a
            href="/contact"
            className="inline-block bg-jaw-yellow text-[#262629] font-semibold px-10 py-5 rounded-[10px] hover:bg-[#e8660a] transition-colors"
          >
            Get a free fee proposal
          </a>
        </div>
      </div>

      {/* email-gate dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-[#2d2d31] border-white/10 text-white sm:max-w-md">
          {done ? (
            <div className="text-center py-4">
              <DialogHeader>
                <DialogTitle className="font-display text-yellow text-3xl text-center">Your download is ready</DialogTitle>
                <DialogDescription className="text-white/70 text-center mt-2">
                  If it didn't start automatically, tap the button below.
                </DialogDescription>
              </DialogHeader>
              <button
                onClick={() => triggerDownload(selected.file)}
                className="mt-6 inline-flex items-center gap-2 bg-jaw-yellow text-[#262629] font-semibold px-7 py-3.5 rounded-[10px] hover:bg-[#e8660a] transition-colors"
              >
                <Download className="w-5 h-5" strokeWidth={2.5} />
                Download again
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-yellow text-3xl">Download the {selected?.format} {selected?.title?.toLowerCase()}</DialogTitle>
                <DialogDescription className="text-white/70">
                  Pop in your details and we'll send the report straight to your screen.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="r-name" className="text-white/90">Name</Label>
                  <Input
                    id="r-name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Your name"
                    className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 h-12 focus-visible:ring-[#ff7a1a]"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="r-email" className="text-white/90">Email</Label>
                  <Input
                    id="r-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 h-12 focus-visible:ring-[#ff7a1a]"
                  />
                  {error && <span className="text-[#ff8f4a] text-sm">{error}</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="r-phone" className="text-white/90">Number</Label>
                  <Input
                    id="r-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="07000 000000"
                    className="bg-[#262629] border-white/15 text-white placeholder:text-white/40 h-12 focus-visible:ring-[#ff7a1a]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 bg-jaw-yellow text-[#262629] font-semibold px-7 py-3.5 rounded-[10px] hover:bg-[#e8660a] transition-colors disabled:opacity-70"
                >
                  {submitting ? 'Sending…' : (<><Download className="w-5 h-5" strokeWidth={2.5} /> Get the report</>)}
                </button>
                <p className="text-white/40 text-xs text-center">We'll only use your email to help with your enquiry.</p>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default ReportsPage;
